import { useEffect, useState, useCallback } from 'react';
import {
  Card, Box, BlockStack, InlineStack, Text, Badge, Button, IndexTable, Spinner, Tooltip,
} from '@shopify/polaris';
import apiClient from '../utils/api';

/**
 * Retailer -> brand -> can we act on it.
 *
 * One row per (retailer, brand name as the retailer spells it). The same brand at two
 * retailers is two rows with two independent answers, because a connection is per retailer:
 * Circular being wired up on ShopSimon says nothing about Circular on Macy's.
 *
 * Built from the offers each retailer has actually sent, not from our own brand list. A brand
 * we have never been sent an offer for is not a gap; a brand the retailer sends that we cannot
 * place is the whole reason this table exists — and finding those used to mean reading the SKU
 * listing and inferring it.
 */

/** The three states, what each one means, and the one thing that fixes it. */
const STATE = {
  connected: {
    badge: 'success',
    label: 'Connected',
    tip: 'A 2W brand matches this name and is connected to this retailer, so its offers and '
      + 'orders can be routed.',
  },
  unconnected: {
    badge: 'attention',
    label: 'Not connected',
    tip: 'The brand exists in 2W but has no enabled connection to THIS retailer, so nothing '
      + 'from it can be routed here. A connection is per retailer.',
  },
  none: {
    badge: 'critical',
    label: 'No matching brand',
    tip: 'No 2W brand carries this name. Usually the retailer spells it differently rather '
      + 'than the brand being missing — set the brand\'s Mirakl name, or the per-connection '
      + 'override, before creating a second brand.',
  },
};

/**
 * One "last synced" cell.
 *
 * Three states, not two, because a blank cannot say which of them it is. A brand with no
 * connection has never synced and never could — saying "Not connected" there stops an admin
 * hunting for a schedule that was never going to run. A connected brand that has simply not
 * been synced yet says so, which is a different problem with a different fix.
 */
function LastSynced({ at, connected, what }) {
  if (!connected) {
    return (
      <IndexTable.Cell>
        <Tooltip content="Nothing runs for a brand until it is connected to this retailer.">
          <Text variant="bodySm" tone="subdued">Not connected</Text>
        </Tooltip>
      </IndexTable.Cell>
    );
  }
  if (!at) {
    return (
      <IndexTable.Cell>
        <Tooltip content={`Connected, but no ${what} since. It will fill in on the next run, or press the button for it on the retailer's row above.`}>
          <Text variant="bodySm" tone="subdued">Never</Text>
        </Tooltip>
      </IndexTable.Cell>
    );
  }
  return (
    <IndexTable.Cell>
      <Text variant="bodySm">{new Date(at).toLocaleString()}</Text>
    </IndexTable.Cell>
  );
}

export default function BrandConnectionTable({ onConfigure }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.getBrandConnections();
      setRows(res?.rows || []);
      setError('');
    } catch (err) {
      setError(err.message || 'Could not load brand connections');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const needsAttention = rows.filter((r) => r.state !== 'connected').length;

  return (
    <Card>
      <Box padding="400">
        <BlockStack gap="400">
          <BlockStack gap="050">
            <InlineStack gap="200" blockAlign="center" wrap>
              {/* "Brands", not "Retailer Brands". These ARE the brands — the table is already inside
                  the retailer section, so the qualifier only read as a separate kind of brand. */}
              <Text variant="headingMd" as="h2">Brands</Text>
              {!loading && needsAttention > 0 && (
                <Badge tone="attention">{`${needsAttention} need attention`}</Badge>
              )}
            </InlineStack>
          </BlockStack>

          {error && <Text tone="critical" variant="bodySm">{error}</Text>}

          {loading ? (
            <InlineStack gap="200" blockAlign="center">
              <Spinner size="small" />
              <Text variant="bodySm" tone="subdued">Reading the offers each retailer sent…</Text>
            </InlineStack>
          ) : (
            <IndexTable
              resourceName={{ singular: 'brand', plural: 'brands' }}
              // 1 when empty so the headings survive: at itemCount 0 IndexTable replaces the
              // whole table with emptyState, column names included.
              itemCount={rows.length || 1}
              selectable={false}
              // All left, including Offers. Polaris right-aligns a numeric column by
              // convention, which is right when figures are compared down a column — here
              // there are three of them and the ragged gap it left between the headings read
              // as a layout fault rather than as alignment.
              headings={[
                { title: 'Retailer' },
                { title: 'Brand' },
                { title: 'Orders' },
                { title: 'SKUs' },
                { title: 'Connection status' },
                { title: 'SKUs last synced' },
                { title: 'Orders last synced' },
                { title: 'Tracking last synced' },
                { title: 'Action' },
              ]}
              emptyState={(
                <Box padding="400">
                  <Text variant="bodySm" tone="subdued">
                    No SKUs held yet. Fetch SKUs on a retailer to see which brands it sends.
                  </Text>
                </Box>
              )}
            >
              {rows.map((row, index) => {
                const state = STATE[row.state] ?? STATE.none;
                return (
                  <IndexTable.Row
                    id={`${row.marketplaceId}:${row.retailerBrand}`}
                    key={`${row.marketplaceId}:${row.retailerBrand}`}
                    position={index}
                  >
                    <IndexTable.Cell>
                      <Text variant="bodySm">{row.retailerName}</Text>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      {/* The RETAILER's spelling, because that is the string that has to
                          match ours for anything to route. */}
                      <Text variant="bodySm" fontWeight="semibold">{row.retailerBrand}</Text>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      {/* Orders that reached this brand, or — where nothing matched the name —
                          orders lost because of it. Zero against an unconnected brand means
                          none have arrived yet, not that nothing is at stake. */}
                      <Text variant="bodySm" numeric>{row.orders}</Text>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <Text variant="bodySm" numeric>{row.skus}</Text>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <InlineStack gap="150" blockAlign="center" wrap>
                        <Tooltip content={state.tip}>
                          <Badge tone={state.badge}>{state.label}</Badge>
                        </Tooltip>
                        {/* Alongside the status, not folded into it. The wiring really is
                            there, so it reads as connected — but every read and write for
                            that brand fails until the app is back. */}
                        {row.shopifyAppInstalled === false && (
                          <Tooltip content={`The Shopify app is not installed on ${row.shopDomain || 'this store'}, so reads and writes for this brand will fail until it is.`}>
                            <Badge tone="warning">Shopify app missing</Badge>
                          </Tooltip>
                        )}
                      </InlineStack>
                    </IndexTable.Cell>
                    {/* Per CONNECTION, not per retailer. The SKU pull is account-wide but is
                        stamped only on the connections whose offers it actually read, and the
                        order fetch and tracking push are per brand — a retailer-level figure
                        would tell one brand its catalogue was current because a different
                        brand on the same account had been read. */}
                    <LastSynced at={row.lastSkuRefresh} connected={row.state === 'connected'} what="SKUs have been read from this retailer" />
                    <LastSynced at={row.lastOrderSync} connected={row.state === 'connected'} what="orders have been collected for this brand" />
                    <LastSynced at={row.lastTrackingPush} connected={row.state === 'connected'} what="tracking has been sent to this retailer" />
                    <IndexTable.Cell>
                      {row.brandId ? (
                        <Button size="micro" onClick={() => onConfigure?.(row)}>
                          {row.state === 'connected' ? 'View brand' : 'Configure'}
                        </Button>
                      ) : (
                        // No brand to open. Sending them to a Configure screen that cannot
                        // exist is worse than saying plainly what has to happen first.
                        <Text variant="bodySm" tone="subdued">Map or create the brand</Text>
                      )}
                    </IndexTable.Cell>
                  </IndexTable.Row>
                );
              })}
            </IndexTable>
          )}
        </BlockStack>
      </Box>
    </Card>
  );
}
